import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, Check } from 'lucide-react';
import formatImageSrc from '../../lib/formatImageSrc';

/**
 * People suggestions component for onboarding
 * Shows suggested users with follow buttons
 */
const PeopleSuggestions = ({ users = [], onFollow, currentUserId, initialFollowState = {} }) => {
  const [followState, setFollowState] = useState(initialFollowState);
  const [loading, setLoading] = useState({});

  // Update followState when initialFollowState changes (when users are loaded)
  useEffect(() => {
    setFollowState(initialFollowState);
  }, [initialFollowState]);

  const handleFollow = async (userId) => {
    if (loading[userId]) return;

    setLoading(prev => ({ ...prev, [userId]: true }));
    
    // Optimistic update
    const isCurrentlyFollowing = followState[userId] || false;
    setFollowState(prev => ({ ...prev, [userId]: !isCurrentlyFollowing }));

    try {
      await onFollow(userId, !isCurrentlyFollowing);
    } catch (error) {
      // Revert on error
      console.error('Follow error:', error);
      setFollowState(prev => ({ ...prev, [userId]: isCurrentlyFollowing }));
    } finally {
      setLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 text-sm">No suggestions available right now</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {users.map((user) => {
        const isFollowing = followState[user.id] || false;
        const isLoading = loading[user.id] || false;
        const isCurrentUser = user.id === currentUserId;

        if (isCurrentUser) return null;

        return (
          <div
            key={user.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-3">
              {/* Avatar */}
              <Link
                to={`/profile/${user.id}`}
                className="flex-shrink-0 w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center hover:ring-2 hover:ring-passion-red hover:ring-opacity-50 transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                {user.image_url ? (
                  <img
                    src={formatImageSrc(user.image_url)}
                    alt={user.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-gray-500 font-semibold text-lg">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </Link>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <Link
                  to={`/profile/${user.id}`}
                  className="font-semibold text-gray-900 hover:text-passion-red transition-colors block truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {user.name}
                </Link>
                <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                {user.review_count > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {user.review_count} review{user.review_count !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              {/* Follow Button */}
              <button
                onClick={() => handleFollow(user.id)}
                disabled={isLoading}
                className={`
                  follow-btn flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                  ${isFollowing
                    ? 'bg-white border border-passion-red text-passion-red hover:bg-red-50'
                    : 'bg-passion-red text-white hover:bg-red-600'
                  }
                  ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                aria-label={`${isFollowing ? 'Unfollow' : 'Follow'} ${user.name}`}
              >
                {isFollowing ? (
                  <Check size={14} aria-hidden="true" />
                ) : (
                  <UserPlus size={14} aria-hidden="true" />
                )}
                <span>{isFollowing ? 'Following' : 'Follow'}</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PeopleSuggestions;

